import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { DashboardTileLayoutConfigurationInput } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { DashboardTile } from '../models/dashboard-tile.model';
import { Dashboard, getDashboardsVariables } from '../models/dashboard.model';
import { getDashboardTiles } from '../queries/dashboard-tiles.graphql';
import { getDashboards } from '../queries/dashboards.graphql';
import { DeleteDashboardMutation } from '../queries/delete-dashboards.graphql';
import { DeleteDashboard, DeleteDashboardVariables } from '../queries/generated/DeleteDashboard';
import { GetDashboards, GetDashboardsVariables } from '../queries/generated/GetDashboards';
import { GetDashboardTiles, GetDashboardTilesVariables } from '../queries/generated/GetDashboardTiles';
import { SaveDashboards, SaveDashboardsVariables } from '../queries/generated/SaveDashboards';
import {
   SaveDashboardTileLayoutConfigurations,
   SaveDashboardTileLayoutConfigurationsVariables,
} from '../queries/generated/SaveDashboardTileLayoutConfigurations';
import { saveDashboardTileLayoutConfigurations } from '../queries/save-dashboard-tile-layout-configuration.graphql';
import { SaveDashboardsMutation } from '../queries/saveDashboard.graphql';

@Injectable({ providedIn: 'root' })
export class DashboardApiService {
   constructor(private apiService: LcsApiService) {}

   getDashboards(currentUserId: number): Observable<ExecutionResult<GetDashboards>> {
      const filterExpression: FilterExpression = {
         filterOptions: [
            {
               field: 'userId',
               operation: FilterOperations.EqualTo,
               value: currentUserId,
            },
         ],
         logicalOperator: LogicalOperators.And,
         subExpressions: [],
      };
      const variables = getDashboardsVariables(filterExpression);
      return this.apiService.query<GetDashboards, GetDashboardsVariables>({
         query: getDashboards,
         variables,
      });
   }

   getDashboard(currentUserId: number, dashboardId: number): Observable<ExecutionResult<GetDashboards>> {
      const filterExpression: FilterExpression = {
         filterOptions: [
            {
               field: 'userId',
               operation: FilterOperations.EqualTo,
               value: currentUserId,
            },
            {
               field: 'dashboardId',
               operation: FilterOperations.EqualTo,
               value: dashboardId,
            },
         ],
         logicalOperator: LogicalOperators.And,
         subExpressions: [],
      };
      const variables = getDashboardsVariables(filterExpression);
      return this.apiService.query<GetDashboards, GetDashboardsVariables>({
         query: getDashboards,
         variables,
      });
   }

   addDashboard(name: string, currentUserId: number): Observable<ExecutionResult<SaveDashboards>> {
      return this.apiService.query<SaveDashboards, SaveDashboardsVariables>({
         query: SaveDashboardsMutation,
         variables: {
            dashboards: [
               {
                  userId: currentUserId,
                  name,
                  fields: ['name', 'userId'],
               },
            ],
         },
      });
   }

   renameDashboard(name: string, selectedDashboardId: number): Observable<ExecutionResult<SaveDashboards>> {
      return this.apiService.query<SaveDashboards, SaveDashboardsVariables>({
         query: SaveDashboardsMutation,
         variables: {
            dashboards: [
               {
                  dashboardId: selectedDashboardId,
                  name,
                  fields: ['name'],
               },
            ],
         },
      });
   }

   deleteDashboard(selectedDashboardId: number): Observable<ExecutionResult<DeleteDashboard>> {
      return this.apiService.query<DeleteDashboard, DeleteDashboardVariables>({
         query: DeleteDashboardMutation,
         variables: {
            id: selectedDashboardId,
         },
      });
   }

   updateDashboardTileOnSelectedDashboard(
      selectedDashboard: Dashboard,
      dashboardTile: DashboardTile
   ): Observable<ExecutionResult<GetDashboardTiles>> {
      const filterExpression: FilterExpression = {
         filterOptions: [
            {
               field: 'dashboardTileId',
               operation: FilterOperations.EqualTo,
               value: dashboardTile.dashboardTileId,
            },
            {
               field: 'dashboardId',
               operation: FilterOperations.EqualTo,
               value: selectedDashboard.dashboardId,
            },
         ],
         logicalOperator: LogicalOperators.And,
         subExpressions: [],
      };
      const variables = getDashboardsVariables(filterExpression);
      return this.apiService.query<GetDashboardTiles, GetDashboardTilesVariables>({
         query: getDashboardTiles,
         variables,
      });
   }

   saveDashboardTileLayoutConfigurations(
      dashboardTileLayoutConfigurationInput: DashboardTileLayoutConfigurationInput[],
      selectedDashboard: Dashboard,
      columnLayoutSize: number
   ): Observable<ExecutionResult<SaveDashboardTileLayoutConfigurations>> {
      return this.apiService.query<
         SaveDashboardTileLayoutConfigurations,
         SaveDashboardTileLayoutConfigurationsVariables
      >({
         query: saveDashboardTileLayoutConfigurations,
         variables: {
            dashboardTileLayoutConfigurations: dashboardTileLayoutConfigurationInput,
            dashboard: {
               dashboardId: selectedDashboard.dashboardId,
               lastSavedColumnLayoutSize: columnLayoutSize,
               fields: ['dashboardId', 'lastSavedColumnLayoutSize'],
            },
         },
      });
   }
}
